import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'

/**
 * 浏览器网络请求工具类
 * 使用 fetch API 替代 Node.js 的 request 模块
 */
export class BrowserNet {
  private baseUrl: string = ""
  private static instance: BrowserNet | undefined

  private constructor() {
    this.baseUrl = new BaseWebUrl().baseCg.replace(/\/$/, '')
  }

  /** 获取全局单例，避免重复创建 fetch / XHR 上传工具。 */
  public static getInstance(): BrowserNet {
    if (!this.instance) {
      this.instance = new BrowserNet()
    }
    return this.instance
  }

  /** 设置默认业务域名，便于在不同接口环境下复用。 */
  public setBaseUrl(url: string): void {
    this.baseUrl = url
  }

  /** 发送 GET 请求，可选择传入完整 URL。 */
  public async requestGet(path: string, allUrl: boolean = false): Promise<string> {
    const url = allUrl ? path : this.baseUrl + path
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.text()
  }

  /** 发送 `application/x-www-form-urlencoded` 的 POST 请求。 */
  public async requestPost(path: string, option: Record<string, any>): Promise<string> {
    const url = this.baseUrl + path
    const formData = new URLSearchParams()

    for (const key in option) {
      formData.append(key, String(option[key]))
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.text()
  }

  /** 发送 PATCH 请求。 */
  public async requestPatch(path: string): Promise<string> {
    const url = this.baseUrl + path

    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.text()
  }

  /** 使用 3.0 兼容 multipart 格式上传文件并回调上传进度。 */
  public uploadWithProgress(
    file: File | Blob,
    fileName: string,
    uploadUrl: string,
    onProgress?: (percent: number) => void,
    onState?: (status: 'load' | 'timeout' | 'abort' | 'error', detail: string) => void,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const boundary = this.randomBoundary(15)
      xhr.timeout = 120000

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      }

      xhr.onload = () => {
        onState?.('load', `HTTP ${xhr.status} · ${fileName}`)
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.responseText)
        } else {
          reject(new Error(`Upload failed! status: ${xhr.status}`))
        }
      }

      xhr.onerror = () => {
        onState?.('error', fileName)
        reject(new Error('Network error'))
      }

      xhr.ontimeout = () => {
        onState?.('timeout', fileName)
        reject(new Error('Upload timeout'))
      }

      xhr.onabort = () => {
        onState?.('abort', fileName)
        reject(new Error('Upload aborted'))
      }

      xhr.withCredentials = true
      xhr.open('POST', uploadUrl)
      xhr.setRequestHeader('Content-Type', `multipart/form-data;charset=utf-8;boundary=${boundary}`)

      BrowserFileUtils.blobToArrayBuffer(file)
        .then((buffer) => {
          const body = this.buildLegacyMultipartBody(new Uint8Array(buffer), fileName, boundary)
          xhr.send(body)
        })
        .catch((error) => {
          reject(error instanceof Error ? error : new Error('File read error'))
        })
    })
  }

  private buildLegacyMultipartBody(
    fileBytes: Uint8Array,
    fileName: string,
    boundary: string,
  ): Blob {
    const encoder = new TextEncoder()
    const chunks: BlobPart[] = []
    const fileBuffer = fileBytes.buffer.slice(fileBytes.byteOffset, fileBytes.byteOffset + fileBytes.byteLength) as ArrayBuffer
    chunks.push(encoder.encode(`\n--${boundary}\n`))
    chunks.push(encoder.encode(`Content-Disposition:form-data;name="file";filename="${fileName}"\n`))
    chunks.push(encoder.encode('Content-Type:application/octet-stream\n\n'))
    chunks.push(fileBuffer)
    chunks.push(encoder.encode(`\n--${boundary}--\n`))
    return new Blob(chunks, { type: 'application/octet-stream' })
  }

  /** 生成随机边界串，用于兼容旧上传参数构造。 */
  private randomBoundary(length: number): string {
    const chars = '123456789abcdef'
    let result = ''
    const usedChars = new Set<string>()
    
    while (result.length < length && usedChars.size < chars.length) {
      const pos = Math.floor(Math.random() * chars.length)
      const char = chars[pos]
      if (!usedChars.has(char)) {
        usedChars.add(char)
        result += char
      }
    }
    
    return result
  }
}

export default BrowserNet
